import {ApiClient} from '@hconnect/apiclient'
import axios, {AxiosError} from 'axios'
import {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {EquipmentPlain} from '../types'

const ITEMS_PER_RESPONSE = 100

interface UseEquipmentByMainEquipmentOptions<T> {
  enabled?: boolean
  select?: (data: EquipmentPlain[]) => T
  onSuccess?: (data: T) => void
  onError?: (error: AxiosError) => void
}

export const useEquipmentByMainEquipment = <T = EquipmentPlain[]>(
  plantId: string,
  searchPattern: string | undefined,
  mainEquipmentId?: string,
  options?: UseEquipmentByMainEquipmentOptions<T>
) => {
  const {
    i18n: {language}
  } = useTranslation()
  const {enabled = true, select, onSuccess, onError} = options || {}

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleFetchEquipment = useCallback(async () => {
    if (!plantId || !mainEquipmentId || !enabled) return

    setIsLoading(true)

    try {
      const path = `/plantconfig/${plantId}/equipmentsById/${mainEquipmentId}`
      const params = {
        params: {
          language,
          ...(searchPattern && {searchPattern}),
          itemsPerResponse: ITEMS_PER_RESPONSE
        }
      }

      const response = await ApiClient.axiosInstance.get<EquipmentPlain[]>(path, params)

      setData(() => {
        const transformedData = select ? select(response.data) : (response.data as T)

        onSuccess?.(transformedData)
        setError(null)
        return transformedData
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setError(error)
        onError?.(error)
      } else {
        console.error('Unexpected error:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [plantId, mainEquipmentId, searchPattern, language, enabled, select, onSuccess, onError])

  useEffect(() => {
    if (plantId && mainEquipmentId && enabled) {
      void handleFetchEquipment()
    }
  }, [enabled, handleFetchEquipment, mainEquipmentId, plantId])

  return {
    data: data as T,
    error,
    isLoading,
    refetch: handleFetchEquipment
  }
}
